import { useMemo } from 'react';

import { STEP_CATALOGUE_GC_PAGE_SIZE } from 'constants/appConstants';
import {
  OPERATION_COLLECTION,
  REGULAR_COLLECTIONS,
} from 'constants/gcConstants';
import {
  G_OPERATION_QUERY_KEY,
  M_OPERATION_QUERY_KEY,
  M_SEARCH_OPERATION_QUERY_KEY,
} from 'constants/queryKeys';
import { useCatalogueDropdown } from 'hooks/useCatalogueDropdown';
import { useMOrgId } from 'hooks/useMOrgId';
import { GOperation } from 'interfaces/IGlobalCatalogue';
import { MOperation } from 'interfaces/IMasterDb';
import { composeList } from 'utils/catalogueMergeUtils';

import { StepSelectorOption } from './stepConfig';

const toStepSelectorOption = (item: GOperation): StepSelectorOption => ({
  label: item.name,
  item: item.name,
  globalId: item.globalId,
  rate: 1,
  cost: item.cost ?? null,
  unit: item.operationUnit ?? null,
  co2eScope1: item.co2eScope1 ?? null,
  co2eScope2: item.co2eScope2 ?? null,
  co2eScope3: item.co2eScope3 ?? null,
  co2eUnit: item.co2eUnit ?? null,
});

export const useOperationCatalogue = (gOrgId: string | null) => {
  const mOrgId = useMOrgId();

  const {
    items,
    isLoading,
    isSearchFetching,
    isPaginating: isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  } = useCatalogueDropdown<GOperation, MOperation, GOperation>({
    gOrgId,
    mOrgId,
    gCollectionKey: OPERATION_COLLECTION,
    mCollectionKey: OPERATION_COLLECTION,
    gSearchCollectionName: REGULAR_COLLECTIONS.operation,
    gQueryKey: [G_OPERATION_QUERY_KEY],
    mQueryKey: [M_OPERATION_QUERY_KEY],
    searchQueryKey: [M_SEARCH_OPERATION_QUERY_KEY],
    mergeItems: composeList<GOperation, MOperation>,
    gPageSize: STEP_CATALOGUE_GC_PAGE_SIZE,
  });

  const operationOptions = useMemo(
    () => items.map(toStepSelectorOption),
    [items]
  );

  return {
    operationOptions,
    isLoading,
    isSearchFetching,
    hasNextPage: !!hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    hasFetchNextPageError,
    searchTerm,
    onSearch,
    clearSearch,
  };
};
